import Vue from 'vue';
import Vuex from 'vuex';
import Mapbox from 'mapbox-gl';
import { addHours } from 'date-fns';
import { getRoute } from '@/api/otp';
import { getAdresseringReverseGeocode } from '@/api/dawa';
import { transitMode, getTransitMode, tripType } from '@/util/otp';
import {
  getItineraryModes,
  fixBicycleParkItineraries,
  decorateStepsWithFarligeKryds,
} from '@/util/itinerary';

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    user: null,
    trip: {
      id: undefined,
      title: '',
      lastEdit: new Date().toISOString(),
      ud: {
        tripType: tripType.standard,
        fromPlace: undefined,
        toPlace: undefined,
        viaPlace: undefined,
        selectedItineraryIndex: 0,
        startTime: addHours(new Date(), 1).toISOString(),

        /** Bruges for at hente itinerary når ruten loades */
        transportModes: {
          bike: false,
          transit: true,
        },

        /** Bruges for at vise faktiske transportmidler på valgte ruter */
        itineraryModes: [],
      },
      retur: undefined,
    },
    itineraries: [],
    wfs: null,
    loadingItinerary: true,
  },
  mutations: {
    setLoadingItinerary(state, loadingItinerary) {
      state.loadingItinerary = loadingItinerary;
    },
    setTrip(state, trip) {
      state.trip = trip;
    },
    setTripUd(state, ud) {
      state.trip = { ...state.trip, ud };
    },
    setTripRetur(state, retur) {
      state.trip = { ...state.trip, retur };
    },
    setTripType(state, payload) {
      const { tripType, udSelected } = payload;
      const { trip } = state;
      if (udSelected) {
        state.trip = { ...trip, ud: { ...trip.ud, tripType } };
      } else {
        state.trip = { ...trip, retur: { ...trip.retur, tripType } };
      }
    },
    setStartTime(state, payload) {
      const { startTime, udSelected } = payload;
      const { trip } = state;
      if (udSelected) {
        state.trip = { ...trip, ud: { ...trip.ud, startTime } };
      } else {
        state.trip = { ...trip, retur: { ...trip.retur, startTime } };
      }
    },
    toggleTransportMode(state, payload) {
      const { mode, udSelected } = payload;
      const { trip } = state;
      if (udSelected) {
        if (mode === 'bike') {
          state.trip = {
            ...trip,
            ud: {
              ...trip.ud,
              transportModes: {
                ...trip.ud.transportModes,
                bike: !trip.ud.transportModes.bike,
              },
            },
          };
        } else if (mode === 'transit') {
          state.trip = {
            ...trip,
            ud: {
              ...trip.ud,
              transportModes: {
                ...trip.ud.transportModes,
                transit: !trip.ud.transportModes.transit,
              },
            },
          };
        }
      } else {
        if (mode === 'bike') {
          state.trip = {
            ...trip,
            retur: {
              ...trip.retur,
              transportModes: {
                ...trip.retur.transportModes,
                bike: !trip.retur.transportModes.bike,
              },
            },
          };
        } else if (mode === 'transit') {
          state.trip = {
            ...trip,
            retur: {
              ...trip.retur,
              transportModes: {
                ...trip.retur.transportModes,
                transit: !trip.retur.transportModes.transit,
              },
            },
          };
        }
      }
    },
    setToPlace(state, payload) {
      const { point, adgangsAdresse, udSelected } = payload;
      const { trip } = state;
      if (udSelected) {
        state.trip = {
          ...trip,
          ud: { ...trip.ud, toPlace: { point, adgangsAdresse } },
        };
      } else {
        state.trip = {
          ...trip,
          retur: { ...trip.retur, toPlace: { point, adgangsAdresse } },
        };
      }
    },
    setFromPlace(state, payload) {
      const { point, adgangsAdresse, udSelected } = payload;
      const { trip } = state;
      if (udSelected) {
        state.trip = {
          ...trip,
          ud: { ...trip.ud, fromPlace: { point, adgangsAdresse } },
        };
      } else {
        state.trip = {
          ...trip,
          retur: { ...trip.retur, fromPlace: { point, adgangsAdresse } },
        };
      }
    },
    setViaPlace(state, payload) {
      const { viaPlace, udSelected } = payload;
      const { trip } = state;
      if (udSelected) {
        state.trip = {
          ...trip,
          ud: { ...trip.ud, viaPlace },
        };
      } else {
        state.trip = {
          ...trip,
          retur: { ...trip.retur, viaPlace },
        };
      }
    },
    setItineraries(state, itineraries) {
      itineraries.forEach(itinerary => {
        itinerary.bounds = new Mapbox.LngLatBounds();
        itinerary.advarselData = {
          type: 'FeatureCollection',
          features: [],
        };
        itinerary.cykelstier = {
          type: 'FeatureCollection',
          features: [],
        };
      });

      state.itineraries = itineraries;
    },
    setSelectedItineraryIndex(state, payload) {
      const { selectedItineraryIndex, udSelected } = payload;
      if (udSelected) {
        state.trip = {
          ...state.trip,
          ud: { ...state.trip.ud, selectedItineraryIndex },
        };
      } else {
        state.trip = {
          ...state.trip,
          retur: { ...state.trip.retur, selectedItineraryIndex },
        };
      }
    },
    removeItineraries(state) {
      state.itineraries = [];
    },
    updateUser(state, user) {
      state.user = user;
    },
    setWfs(state, wfs) {
      state.wfs = wfs;
    },
    stepClicked() {},

    setItineraryModes(state, payload) {
      const { itinerary, udSelected } = payload;
      const itineraryModes = getItineraryModes(itinerary);
      const { trip } = state;
      if (udSelected) {
        state.trip = { ...trip, ud: { ...trip.ud, itineraryModes } };
      } else {
        state.trip = { ...trip, retur: { ...trip.retur, itineraryModes } };
      }
    },
  },
  actions: {
    getItineraries(context, payload) {
      context.commit('setLoadingItinerary', true);
      return new Promise((resolve, reject) => {
        let {
          from,
          to,
          numItineraries = 3,
          startTime = new Date(),
          transportModes,
          walkReluctance,
          udSelected,
          selectedItineraryIndex,
        } = payload;
        // if (!transportModes.transit) {
        //   selectedItineraryIndex = 0;
        // }
        const mode = getTransitMode(transportModes);
        getRoute({
          from,
          to,
          startTime,
          numItineraries,
          mode,
          walkReluctance,
        }).then(route => {
          if (route.error) {
            // console.error('no route found');
            context.commit('setLoadingItinerary', false);
            reject(route.error);
          } else {
            let { itineraries } = route.plan;
            itineraries = fixBicycleParkItineraries(itineraries);
            itineraries = decorateStepsWithFarligeKryds(itineraries);
            if (mode !== 'BICYCLE') {
              context.commit('setItineraries', itineraries);
              if (numItineraries === 3) {
                context.commit('setSelectedItineraryIndex', {
                  selectedItineraryIndex,
                  udSelected,
                });
              }
              context.commit('setLoadingItinerary', false);
              resolve(itineraries);
            } else {
              const getSafeRoute = getRoute({
                from,
                to,
                startTime,
                numItineraries,
                mode,
                walkReluctance,
                triangleSafetyFactor: 1,
                triangleTimeFactor: 0,
              });
              const getAverageRoute = getRoute({
                from,
                to,
                startTime,
                numItineraries,
                mode,
                walkReluctance,
                triangleSafetyFactor: 0.5,
                triangleTimeFactor: 0.5,
              });

              Promise.all([getSafeRoute, getAverageRoute]).then(response => {
                const [safeRoute, averageRoute] = response;
                context.commit('setLoadingItinerary', false);
                resolve(response);
                if (safeRoute.plan) {
                  itineraries = [...itineraries, ...safeRoute.plan.itineraries];
                }
                if (averageRoute.plan) {
                  itineraries = [
                    ...itineraries,
                    ...averageRoute.plan.itineraries,
                  ];
                }
                // Sammenligner geometri for cykeletapper, og forkaster itinerary dersom lik:
                itineraries = itineraries.filter((itinerary, index) => {
                  return (
                    itineraries.findIndex(
                      a =>
                        a.legs.find(leg => (leg.mode = 'BICYCLE')).legGeometry
                          .points ===
                        itinerary.legs.find(leg => (leg.mode = 'BICYCLE'))
                          .legGeometry.points
                    ) === index
                  );
                });
                itineraries = decorateStepsWithFarligeKryds(itineraries);
                context.commit('setItineraries', itineraries);
                context.commit('setSelectedItineraryIndex', {
                  selectedItineraryIndex,
                  udSelected,
                });
                context.commit('setLoadingItinerary', false);
                resolve(itineraries);
              });
            }
          }
        });
      });
    },
    getItinerariesWithCustomCycleRoute(context, payload) {
      context.commit('setLoadingItinerary', true);
      const {
        cycleTo,
        from,
        to,
        numItineraries = 3,
        walkReluctance,
        udSelected,
        selectedItineraryIndex,
        startTime,
      } = payload;
      const { lat, lng } = cycleTo;
      const cycleToLatLng = { lat, lng };
      // Make new transit route from bus stop
      const cycleRoutePayload = {
        from,
        to: cycleToLatLng,
        startTime: new Date(startTime),
        mode: transitMode.bicycle,
        numItineraries: 1,
        walkReluctance,
      };
      return new Promise((resolve, reject) => {
        getRoute(cycleRoutePayload).then(cycleRoute => {
          if (cycleRoute.error) {
            console.error('no cycleRoute found');
            context.commit('setLoadingItinerary', false);
            reject(cycleRoute.error);
          } else {
            // get transit route
            const cykleItinerary = cycleRoute.plan.itineraries[0];
            const getTransitRoutePayload = {
              mode: transitMode.transitWalk,
              from: cycleToLatLng,
              to,
              startTime: new Date(cykleItinerary.endTime + 120000),
              numItineraries: 3,
              walkReluctance,
            };
            getRoute(getTransitRoutePayload).then(transitRoute => {
              if (transitRoute.error) {
                console.error('no transitRoute found');
                context.commit('setLoadingItinerary', false);
                reject(transitRoute.error);
              } else {
                // Stitch together into new itineray
                let { itineraries } = transitRoute.plan;
                const cycleLeg = cykleItinerary.legs.find(
                  leg => leg.mode === 'BICYCLE'
                );
                itineraries.forEach(i => {
                  const { from: transitFrom, startTime } = i.legs[0];
                  i.duration += cykleItinerary.duration;
                  i.startTime -= cykleItinerary.duration * 1000;
                  i.legs.unshift({
                    ...cycleLeg,
                    to: { ...transitFrom, arrival: transitFrom.departure },
                    endTime: startTime,
                    startTime: startTime - cycleLeg.duration * 1000,
                    from: {
                      ...cycleLeg.from,
                      departure: startTime - cycleLeg.duration * 1000,
                      arrival: startTime,
                    },
                  });
                });
                itineraries = fixBicycleParkItineraries(itineraries);
                itineraries = decorateStepsWithFarligeKryds(itineraries);
                // Commit
                context.commit('setItineraries', itineraries);
                if (numItineraries === 3) {
                  context.commit('setSelectedItineraryIndex', {
                    selectedItineraryIndex,
                    udSelected,
                  });
                }
                context.commit('setLoadingItinerary', false);
                resolve(itineraries);
              }
            });
          }
        });
      });
    },
    getItinerariesWithCustomCycleRouteAtEnd(context, payload) {
      context.commit('setLoadingItinerary', true);
      // get transit route to where bikes were parked
      const {
        cycleFrom,
        from,
        to,
        startTime,
        numItineraries = 3,
        walkReluctance,
        udSelected,
        // selectedItineraryIndex,
      } = payload;
      const getTransitRoutePayload = {
        mode: transitMode.transitWalk,
        from,
        to: cycleFrom,
        startTime,
        numItineraries: 3,
        walkReluctance,
      };
      return new Promise((resolve, reject) => {
        getRoute(getTransitRoutePayload).then(transitRoute => {
          if (transitRoute.error) {
            console.error('no transitRoute found');
            context.commit('setLoadingItinerary', false);
            reject(transitRoute.error);
          } else {
            let { itineraries } = transitRoute.plan;
            itineraries = decorateStepsWithFarligeKryds(itineraries);
            const selectedItineraryIndex = 0;

            const { lat, lng } = cycleFrom;
            const cycleFromLatLng = { lat, lng };

            // Make new cycle route from bus stop
            const cycleRoutePayload = {
              from: cycleFromLatLng,
              to,
              startTime: new Date(), // Lige meget, findes senere ved at subtrahere duration fra busholdeplats
              mode: transitMode.bicycle,
              numItineraries: 1,
              walkReluctance,
            };
            getRoute(cycleRoutePayload).then(cycleRoute => {
              if (cycleRoute.error) {
                console.error('no cycleRoute found');
                context.commit('setLoadingItinerary', false);
                reject(cycleRoute.error);
              } else {
                // Stitch together into new itineray

                const cykleItinerary = cycleRoute.plan.itineraries[0];
                let cycleLeg = cykleItinerary.legs.find(
                  leg => leg.mode === 'BICYCLE'
                );
                if (!cycleLeg) {
                  cycleLeg = cykleItinerary.legs[0];
                }
                itineraries.forEach(i => {
                  const { to: transitTo, endTime } = i.legs[i.legs.length - 1];
                  i.duration += cykleItinerary.duration;
                  i.endTime += cykleItinerary.duration * 1000;
                  i.legs.push({
                    ...cycleLeg,
                    from: transitTo,
                    startTime: endTime,
                    endTime: endTime + cycleLeg.duration * 1000,
                    to: {
                      ...cycleLeg.to,
                      arrival: endTime + cycleLeg.duration * 1000,
                    },
                  });
                  // i.legs = [
                  //   ...i.legs,
                  //   ...cykleItinerary.legs.map((leg, i) => {
                  //     const newLeg = { ...leg };
                  //     if (i === 0) {
                  //       newLeg.from = transitTo;
                  //       newLeg.startTime = endTime;
                  //     }
                  //     if (i === cykleItinerary.legs - 1) {
                  //       newLeg.to = {
                  //         ...cycleLeg.to,
                  //         arrival: endTime + cycleLeg.duration * 1000,
                  //       };
                  //     }
                  //     return newLeg;
                  //   }),
                  // ];
                });

                // set destination of cycle leg to origin of next leg because
                // destination name of cycle leg is "destination", not very helpful
                // cycleLeg.to = itineraries[0].legs[1].from;
                // cycleLeg.to.arrival = cycleLeg.to.departure;
                // cycleLeg.endTime = cycleLeg.to.departure;
                // cycleLeg.startTime =
                //   cycleLeg.to.arrival - cycleLeg.duration * 1000;
                // Commit
                context.commit('setItineraries', itineraries);
                if (numItineraries === 3) {
                  context.commit('setSelectedItineraryIndex', {
                    selectedItineraryIndex,
                    udSelected,
                  });
                }
                context.commit('setLoadingItinerary', false);
                resolve(itineraries);
              }
            });
          }
        });
      });
    },
    getFromPlace(context, payload) {
      return context.dispatch('getPlace', { ...payload, place: 'fromPlace' });
    },
    getToPlace(context, payload) {
      return context.dispatch('getPlace', { ...payload, place: 'toPlace' });
    },
    setToPlace(context, toPlace) {
      return new Promise(resolve => {
        context.commit('setToPlace', toPlace);
        resolve(toPlace);
      });
    },
    getPlace(context, payload) {
      const { lat, lng, place, udSelected } = payload;
      return new Promise((resolve, reject) => {
        getAdresseringReverseGeocode(lng, lat).then(adresse => {
          if (adresse.error) {
            console.error('no address found');
            reject(adresse.error);
          } else {
            if (place === 'fromPlace') {
              context.commit('setFromPlace', {
                point: { lat, lng },
                adgangsAdresse: adresse,
                udSelected,
              });
            } else {
              context.commit('setToPlace', {
                point: { lat, lng },
                adgangsAdresse: adresse,
                udSelected,
              });
            }
            resolve(adresse);
          }
        });
      });
    },
  },
});
